import { z } from "zod";

/**
 * Board-style item types (spec §5.2 `questions.item_type`, §13.4a). Each has its own payload schema.
 * A format pack lists which of these its exam allows; the generator may produce nothing else.
 */
export const ITEM_TYPES = [
  "mcq",
  "sata",
  "bowtie",
  "matrix",
  "highlight",
  "cloze",
  "drag_drop_cloze",
  "trend",
] as const;
export const itemTypeSchema = z.enum(ITEM_TYPES);
export type ItemType = z.infer<typeof itemTypeSchema>;

/** How an item is scored (NCSBN, §13.4a): 0/1, +/− partial credit, or rationale scoring for linked blanks. */
export const scoringRuleSchema = z.enum(["zero_one", "plus_minus", "rationale"]);
export type ScoringRule = z.infer<typeof scoringRuleSchema>;

const id = z.string().min(1).max(40);
const text = z.string().min(1);
const option = z.object({ id, text });

/** Every rationale explains the key AND each distractor (spec §13.4b). */
const rationaleSchema = z.object({
  correct: text,
  distractors: z.record(id, text),
});

const base = {
  stem: text,
  rationale: rationaleSchema,
  scoring: scoringRuleSchema,
};

const ensureKeysExist = (ids: string[], keys: string[], ctx: z.RefinementCtx, path: string) => {
  for (const k of keys) {
    if (!ids.includes(k)) ctx.addIssue({ code: "custom", message: `Key ${k} is not an option`, path: [path] });
  }
};

export const mcqPayload = z
  .object({ ...base, item_type: z.literal("mcq"), options: z.array(option).min(3).max(6), key: id })
  .superRefine((p, ctx) => ensureKeysExist(p.options.map((o) => o.id), [p.key], ctx, "key"));

export const sataPayload = z
  .object({ ...base, item_type: z.literal("sata"), options: z.array(option).min(5).max(10), keys: z.array(id).min(1) })
  .superRefine((p, ctx) => ensureKeysExist(p.options.map((o) => o.id), p.keys, ctx, "keys"));

/** NGN bow-tie: 1 condition, 2 actions, 2 parameters to monitor, chosen from separate option wells. */
export const bowtiePayload = z.object({
  ...base,
  item_type: z.literal("bowtie"),
  conditions: z.array(option).min(3),
  actions: z.array(option).min(4),
  parameters: z.array(option).min(4),
  key: z.object({ condition: id, actions: z.array(id).length(2), parameters: z.array(id).length(2) }),
});

/** Matrix / grid: rows × columns; single = one choice per row, multiple = any per row. */
export const matrixPayload = z.object({
  ...base,
  item_type: z.literal("matrix"),
  mode: z.enum(["single", "multiple"]),
  rows: z.array(option).min(2),
  columns: z.array(option).min(2),
  key: z.record(id, z.array(id).min(1)),
});

/** Highlight text or table cells that answer the lead-in. */
export const highlightPayload = z.object({
  ...base,
  item_type: z.literal("highlight"),
  segments: z.array(option).min(3),
  keys: z.array(id).min(1),
});

/**
 * Linked blanks scored together under NCSBN rationale scoring (a dyad or triad, e.g. "at risk for [A] as evidenced
 * by [B]"): the group earns its point only when every blank in it is right. Required exactly when scoring is
 * "rationale"; each blank belongs to at most one group; blanks outside a group score on their own.
 */
const rationaleGroups = z.array(z.array(id).min(2).max(3)).min(1).optional();
const checkRationaleGroups = (scoring: string, groups: string[][] | undefined, blankIds: string[], ctx: z.RefinementCtx) => {
  if ((scoring === "rationale") !== !!groups) {
    ctx.addIssue({ code: "custom", message: "rationale_groups are required exactly when scoring is rationale", path: ["rationale_groups"] });
  }
  const seen = new Set<string>();
  for (const g of groups ?? []) {
    for (const b of g) {
      if (!blankIds.includes(b)) ctx.addIssue({ code: "custom", message: `Group blank ${b} doesn't exist`, path: ["rationale_groups"] });
      if (seen.has(b)) ctx.addIssue({ code: "custom", message: `Blank ${b} is in more than one group`, path: ["rationale_groups"] });
      seen.add(b);
    }
  }
};

/** Cloze drop-down: blanks inside a passage, each with its own option list (incl. drop-down rationale). */
const blank = z.object({ id, options: z.array(option).min(2), key: id });
export const clozePayload = z
  .object({
    ...base,
    item_type: z.literal("cloze"),
    passage: text,
    blanks: z.array(blank).min(1),
    rationale_groups: rationaleGroups,
  })
  .superRefine((p, ctx) => checkRationaleGroups(p.scoring, p.rationale_groups, p.blanks.map((b) => b.id), ctx));

/** Drag-and-drop cloze: one shared token pool dropped into blanks (incl. drag-and-drop rationale). */
export const dragDropClozePayload = z
  .object({
    ...base,
    item_type: z.literal("drag_drop_cloze"),
    passage: text,
    tokens: z.array(option).min(2),
    key: z.record(id, id),
    rationale_groups: rationaleGroups,
  })
  .superRefine((p, ctx) => checkRationaleGroups(p.scoring, p.rationale_groups, Object.keys(p.key), ctx));

/** Trend item: data over time (vitals, labs) shown in tabs, answered as MCQ/SATA/matrix. */
export const trendPayload = z.object({
  ...base,
  item_type: z.literal("trend"),
  series: z
    .array(z.object({ label: text, unit: z.string(), points: z.array(z.object({ at: text, value: z.string() })).min(2) }))
    .min(1),
  answer_format: z.enum(["mcq", "sata", "matrix"]),
  options: z.array(option).min(3),
  keys: z.array(id).min(1),
});

export const itemPayloadSchema = z.discriminatedUnion("item_type", [
  mcqPayload,
  sataPayload,
  bowtiePayload,
  matrixPayload,
  highlightPayload,
  clozePayload,
  dragDropClozePayload,
  trendPayload,
]).superRefine((p, ctx) => {
  // NCSBN rationale scoring applies to linked pairs/triads, which only cloze-style items have.
  if (p.scoring === "rationale" && p.item_type !== "cloze" && p.item_type !== "drag_drop_cloze") {
    ctx.addIssue({ code: "custom", message: "Rationale scoring is only for drop-down or drag-and-drop rationale items", path: ["scoring"] });
  }
});
export type ItemPayload = z.infer<typeof itemPayloadSchema>;

/**
 * NGN case studies (plan §7.5, BE12): one client presentation shared by six linked items, each measuring one step of
 * the NCSBN Clinical Judgment Measurement Model in order. The record is built only from the student's material.
 */
export const NCJMM_STEPS = ["recognize_cues", "analyze_cues", "prioritize_hypotheses", "generate_solutions", "take_action", "evaluate_outcomes"] as const;
export const ncjmmStepSchema = z.enum(NCJMM_STEPS);
export type NcjmmStep = z.infer<typeof ncjmmStepSchema>;
export const NCJMM_STEP_LABELS: Record<NcjmmStep, string> = {
  recognize_cues: "Recognize cues",
  analyze_cues: "Analyze cues",
  prioritize_hypotheses: "Prioritize hypotheses",
  generate_solutions: "Generate solutions",
  take_action: "Take action",
  evaluate_outcomes: "Evaluate outcomes",
};
export const CASE_RECORD_TABS = ["nurses_notes", "history", "assessment", "vital_signs", "labs", "orders", "medications"] as const;
export const CASE_RECORD_TAB_LABELS: Record<(typeof CASE_RECORD_TABS)[number], string> = {
  nurses_notes: "Nurses' notes",
  history: "History",
  assessment: "Assessment",
  vital_signs: "Vital signs",
  labs: "Labs",
  orders: "Orders",
  medications: "Medications",
};
export const clientRecordSchema = z.object({
  tabs: z
    .array(
      z.object({
        tab: z.enum(CASE_RECORD_TABS),
        entries: z.array(z.object({ time: z.string().max(40).nullable(), text: z.string().min(1).max(600) })).min(1).max(12),
      }),
    )
    .min(1)
    .max(6),
});
export type ClientRecord = z.infer<typeof clientRecordSchema>;

/** One generated item as stored (minus DB bookkeeping). Every item cites ≥ 1 chunk (§6.4). */
export const generatedItemSchema = z.object({
  payload: itemPayloadSchema,
  source_chunk_ids: z.array(z.string().uuid()).min(1),
  blueprint_tags: z.record(z.string(), z.string()).refine((t) => Object.keys(t).length >= 1, "At least one blueprint tag"),
  difficulty: z.number().min(-3).max(3),
  case_id: z.string().uuid().nullable(),
  /** The clinical judgment step a case-study item measures (null outside case studies). */
  ncjmm_step: ncjmmStepSchema.nullable().default(null),
  versions: z.object({ style_pack: z.string(), format_pack: z.string(), rules: z.string() }),
});
export type GeneratedItem = z.infer<typeof generatedItemSchema>;
